import React from "react";
import { useIntl } from "react-intl";
import { resolveQuizFeedbackThemeId } from "./quizFeedbackThemes";

const KEYFRAMES = `
@keyframes er-qfm-fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes er-qfm-stamp {
	0% { opacity: 0; transform: scale(1.4) rotate(-8deg); }
	55% { opacity: 1; transform: scale(0.92) rotate(2deg); }
	75% { transform: scale(1.04) rotate(-1deg); }
	100% { transform: scale(1) rotate(0); }
}
@keyframes er-qfm-stampWrong {
	0% { opacity: 0; transform: translateY(-40px) rotate(6deg); }
	60% { transform: translateY(4px) rotate(-2deg); }
	100% { opacity: 1; transform: translateY(0) rotate(0); }
}
@keyframes er-qfm-neonFlicker {
	0%, 18%, 22%, 55%, 100% { opacity: 1; }
	20% { opacity: 0.55; }
	57% { opacity: 0.7; }
}
@keyframes er-qfm-neonEntry {
	0% { opacity: 0; transform: translate(-5px, 3px) skewX(-3deg); filter: drop-shadow(4px 0 #ff00c8) drop-shadow(-4px 0 #00e5ff); }
	30% { opacity: 1; transform: translate(4px, -3px) skewX(2deg); }
	60% { transform: translate(-2px, 1px); filter: drop-shadow(-2px 0 #ff00c8); }
	100% { transform: none; filter: none; }
}
@keyframes er-qfm-neonBorder {
	0%, 100% { border-color: #ff00c8; box-shadow: 0 0 24px rgba(255,0,200,0.45), inset 0 0 20px rgba(0,229,255,0.08); }
	50% { border-color: #00e5ff; box-shadow: 0 0 32px rgba(0,229,255,0.5), inset 0 0 24px rgba(255,0,200,0.1); }
}
@keyframes er-qfm-scanMove { from { background-position: 0 0; } to { background-position: 0 8px; } }
@keyframes er-qfm-paperRise { from { opacity: 0; transform: translateY(32px); } to { opacity: 1; transform: translateY(0); } }
@keyframes er-qfm-carnivalSpin {
	0% { opacity: 0; transform: scale(0.2) rotate(-20deg); }
	50% { transform: scale(1.12) rotate(8deg); }
	70% { transform: scale(0.95) rotate(-4deg); }
	100% { opacity: 1; transform: scale(1) rotate(0); }
}
@keyframes er-qfm-confettiFall { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(-140px) rotate(540deg); opacity: 0; } }
@keyframes er-qfm-wobble { 0%, 100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg); } }
@keyframes er-qfm-blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
@keyframes er-qfm-terminalShake { 0%, 100% { transform: translateX(0); } 15% { transform: translateX(-8px); } 30% { transform: translateX(8px); } 45% { transform: translateX(-5px); } 60% { transform: translateX(5px); } }
@keyframes er-qfm-terminalType { from { width: 0; } to { width: 100%; } }
@keyframes er-qfm-blueRise { from { opacity: 0; transform: translateY(48px) scale(0.9); filter: blur(10px); } to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); } }
@keyframes er-qfm-bluePulse { 0%, 100% { box-shadow: 0 0 20px rgba(59,130,246,0.5); } 50% { box-shadow: 0 0 40px rgba(96,165,250,0.8); } }
@keyframes er-qfm-dossierSlide { from { opacity: 0; transform: translateX(60px) skewX(-2deg); } to { opacity: 1; transform: translateX(0) skewX(0); } }
@keyframes er-qfm-clinicalFlash { 0%, 100% { border-left-color: #dc2626; } 50% { border-left-color: #f87171; } }
@keyframes er-qfm-comicSlam { 0% { opacity: 0; transform: scale(2) rotate(-12deg); } 60% { transform: scale(0.95) rotate(3deg); } 100% { opacity: 1; transform: scale(1) rotate(-1deg); } }
@keyframes er-qfm-horrorPulse { 0%, 100% { box-shadow: 0 0 30px rgba(127,29,29,0.5); } 50% { box-shadow: 0 0 50px rgba(220,38,38,0.7); } }
@keyframes er-qfm-goldShine { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
@keyframes er-qfm-stickyDrop { 0% { opacity: 0; transform: rotate(8deg) translateY(-30px); } 70% { transform: rotate(-3deg) translateY(4px); } 100% { opacity: 1; transform: rotate(-2deg) translateY(0); } }
@keyframes er-qfm-clinicalFormalIn {
	0% { opacity: 0; transform: translateY(36px) scale(0.96); filter: blur(6px); clip-path: inset(0 0 92% 0 round 6px); }
	28% { opacity: 1; filter: blur(0); clip-path: inset(0 0 55% 0 round 6px); }
	62% { transform: translateY(-6px) scale(1.01); clip-path: inset(0 0 0 0 round 6px); }
	100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); clip-path: inset(0 0 0 0 round 6px); }
}
@keyframes er-qfm-ecgDraw {
	0% { stroke-dashoffset: 520; opacity: 0; }
	12% { opacity: 1; }
	78% { stroke-dashoffset: 0; opacity: 1; }
	100% { stroke-dashoffset: 0; opacity: 0.55; }
}
@keyframes er-qfm-ecgGlow {
	0%, 100% { filter: drop-shadow(0 0 0 transparent); }
	40% { filter: drop-shadow(0 0 6px currentColor); }
}
@keyframes er-qfm-vitalRing {
	0% { transform: scale(0.55); opacity: 0.7; }
	70% { opacity: 0.15; }
	100% { transform: scale(1.65); opacity: 0; }
}
@keyframes er-qfm-clinicalScan {
	0% { transform: translateY(-120%); opacity: 0; }
	18% { opacity: 0.35; }
	100% { transform: translateY(220%); opacity: 0; }
}
@keyframes er-qfm-clinicalCrossStamp {
	0% { opacity: 0; transform: translate(-50%, -50%) scale(1.7) rotate(-18deg); }
	35% { opacity: 0.18; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
	100% { opacity: 0; transform: translate(-50%, -50%) scale(0.92) rotate(0deg); }
}
`;

function ensureClinicalFormalFonts() {
	if (typeof document === "undefined") return;
	if (document.getElementById("er-qfm-clinical-formal-fonts")) return;
	const link = document.createElement("link");
	link.id = "er-qfm-clinical-formal-fonts";
	link.rel = "stylesheet";
	link.href = "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Oswald:wght@500;600;700&display=swap";
	document.head.appendChild(link);
}

/** Trazo ECG de entrada (ritmo normal vs irregular / plano según resultado). */
function ClinicalFormalEcgTrace({ correct, survey, preview }) {
	if (preview) return null;
	const ok = survey || correct;
	const stroke = ok ? "#10b981" : "#dc2626";
	const d = ok
		? "M0,22 L48,22 L58,22 L66,6 L74,38 L82,14 L90,22 L140,22 L150,22 L158,8 L166,36 L174,16 L182,22 L260,22 L270,22 L278,10 L286,34 L294,18 L302,22 L380,22"
		: "M0,22 L70,22 L82,18 L94,28 L110,10 L128,34 L146,20 L170,22 L210,22 L230,24 L250,20 L280,22 L310,22 L330,23 L360,21 L380,22";
	return (
		<svg
			aria-hidden
			viewBox="0 0 380 44"
			preserveAspectRatio="none"
			style={{
				position: "absolute",
				top: 14,
				left: 0,
				right: 0,
				width: "100%",
				height: 36,
				pointerEvents: "none",
				zIndex: 2,
				color: stroke,
				opacity: 0.9,
			}}
		>
			<path
				d={d}
				fill="none"
				stroke={stroke}
				strokeWidth="2.2"
				strokeLinecap="round"
				strokeLinejoin="round"
				style={{
					strokeDasharray: 520,
					strokeDashoffset: 520,
					animation: "er-qfm-ecgDraw 1.05s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both, er-qfm-ecgGlow 1.2s ease-out 0.2s both",
				}}
			/>
		</svg>
	);
}

function ThemeDecorations({ themeId, correct, survey, preview = false }) {
	const stripeH = preview ? 3 : 7;
	if (themeId === "neon") {
		return (
			<>
				<div style={{
					position: "absolute", inset: 0, pointerEvents: "none", borderRadius: "inherit", zIndex: 0,
					background: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.1) 3px, rgba(0,0,0,0.1) 4px)",
					opacity: 0.55,
				}} />
				<div style={{
					position: "absolute", bottom: -20, left: "8%", right: "8%", height: 2, zIndex: 0,
					background: "linear-gradient(90deg, transparent, #ff00c8, #00e5ff, transparent)",
					opacity: 0.45,
				}} />
			</>
		);
	}
	if (themeId === "playful") {
		const dots = ["#ff2d95", "#ffd60a", "#00d4ff", "#7c3aed", "#ff6b35"];
		return (
			<div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", borderRadius: "inherit" }}>
				{dots.map((c, i) => (
					<span key={i} style={{
						position: "absolute",
						width: 14 + (i % 3) * 6,
						height: 14 + (i % 3) * 6,
						borderRadius: "50%",
						background: c,
						opacity: 0.18,
						top: `${8 + i * 17}%`,
						left: `${5 + i * 18}%`,
						animation: `er-qfm-wobble ${1.2 + i * 0.2}s ease-in-out infinite`,
					}} />
				))}
				{correct && !survey && Array.from({ length: 22 }).map((_, i) => (
					<span key={`c-${i}`} style={{
						position: "absolute",
						left: `${4 + (i * 4.5) % 92}%`,
						bottom: `${6 + (i % 4) * 5}%`,
						width: 5 + (i % 4),
						height: 8 + (i % 3),
						background: dots[i % dots.length],
						borderRadius: i % 2 ? "50%" : 1,
						animation: `er-qfm-confettiFall ${0.85 + (i % 6) * 0.1}s ease-out ${i * 0.035}s both`,
					}} />
				))}
			</div>
		);
	}
	if (themeId === "retro") {
		return (
			<div style={{
				position: "absolute", inset: 0, pointerEvents: "none", borderRadius: "inherit",
				background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, rgba(0,0,0,0.15) 1px, transparent 1px, transparent 3px)",
			}} />
		);
	}
	if (themeId === "glass") {
		return (
			<div style={{
				position: "absolute", top: -40, right: -40, width: 120, height: 120, borderRadius: "50%",
				background: "radial-gradient(circle, rgba(96,165,250,0.35) 0%, transparent 70%)",
				pointerEvents: "none",
			}} />
		);
	}
	if (themeId === "cinematic") {
		return (
			<div style={{
				position: "absolute", top: 12, right: 16, fontSize: 10, fontWeight: 700, letterSpacing: "0.2em",
				color: "rgba(255,255,255,0.35)", fontFamily: "Arial Narrow, Arial, sans-serif",
			}}>
				INFORME · CONFIDENCIAL
			</div>
		);
	}
	if (themeId === "classic") {
		return (
			<div style={{
				position: "absolute", top: 0, left: 0, right: 0, height: stripeH,
				background: survey ? "#6366f1" : (correct ? "#10b981" : "#dc2626"),
				borderRadius: preview ? "8px 8px 0 0" : "16px 16px 0 0",
			}} />
		);
	}
	if (themeId === "clinicalFormal") {
		const accent = survey ? "#10b981" : (correct ? "#10b981" : "#dc2626");
		const soft = survey || correct ? "#cbff97" : "rgba(220,38,38,0.25)";
		return (
			<>
				<div style={{
					position: "absolute", top: 0, left: 0, right: 0, height: preview ? 5 : 8,
					background: accent,
				}} />
				<div style={{
					position: "absolute", top: preview ? 5 : 8, left: 0, right: 0, height: preview ? 3 : 5,
					background: soft,
				}} />
				{!preview && (
					<>
						<div style={{
							position: "absolute", left: 0, right: 0, top: 0, height: "42%",
							background: `linear-gradient(180deg, ${survey || correct ? "rgba(203,255,151,0.35)" : "rgba(254,202,202,0.35)"} 0%, transparent 100%)`,
							pointerEvents: "none",
							animation: "er-qfm-clinicalScan 0.95s ease-out 0.05s both",
							zIndex: 1,
						}} />
						<ClinicalFormalEcgTrace correct={correct} survey={survey} preview={preview} />
						<div style={{
							position: "absolute", top: "42%", left: "50%",
							width: 64, height: 64,
							pointerEvents: "none", zIndex: 1,
							animation: "er-qfm-clinicalCrossStamp 0.9s ease-out 0.25s both",
						}}>
							<div style={{
								position: "absolute", left: "50%", top: "50%",
								width: 36, height: 12, background: accent, borderRadius: 2,
								transform: "translate(-50%, -50%)",
							}} />
							<div style={{
								position: "absolute", left: "50%", top: "50%",
								width: 12, height: 36, background: accent, borderRadius: 2,
								transform: "translate(-50%, -50%)",
							}} />
						</div>
					</>
				)}
			</>
		);
	}
	if (themeId === "corporate") {
		return (
			<div style={{
				position: "absolute", top: 0, left: 0, right: 0, height: preview ? 4 : 5,
				background: correct ? "#1e40af" : "#475569",
			}} />
		);
	}
	if (themeId === "corporate") {
		return (
			<>
				<div style={{ position: "absolute", inset: preview ? 4 : 8, border: "1px solid #94a3b8", pointerEvents: "none", borderRadius: 1 }} />
				<div style={{ position: "absolute", inset: preview ? 6 : 11, border: "1px solid #cbd5e1", pointerEvents: "none", borderRadius: 1 }} />
			</>
		);
	}
	if (themeId === "academic") {
		return (
			<div style={{
				position: "absolute", top: 0, left: 0, right: 0, height: preview ? 3 : 4,
				background: correct ? "#7f1d1d" : "#44403c",
			}} />
		);
	}
	if (themeId === "comic") {
		return (
			<div style={{
				position: "absolute", inset: 0, pointerEvents: "none", borderRadius: "inherit",
				backgroundImage: "radial-gradient(circle, #1e293b 1px, transparent 1px)",
				backgroundSize: "8px 8px", opacity: 0.07,
			}} />
		);
	}
	if (themeId === "horror") {
		return (
			<div style={{
				position: "absolute", inset: 0, pointerEvents: "none", borderRadius: "inherit",
				background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.75) 100%)",
			}} />
		);
	}
	if (themeId === "gold") {
		return (
			<div style={{
				position: "absolute", top: 0, left: 0, right: 0, height: 3,
				background: "linear-gradient(90deg, transparent, #fbbf24, #fef3c7, #fbbf24, transparent)",
				backgroundSize: "200% 100%",
				animation: "er-qfm-goldShine 2.5s linear infinite",
			}} />
		);
	}
	if (themeId === "sticky") {
		return (
			<div style={{
				position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
				width: 56, height: 18, background: "rgba(255,255,255,0.55)",
				boxShadow: "0 1px 3px rgba(0,0,0,0.12)", borderRadius: 2,
			}} />
		);
	}
	return null;
}

function renderIcon(themeId, { correct, survey, preview = false }) {
	const m = preview ? 0.48 : 1;
	const sz = (n) => Math.round(n * m);
	if (themeId === "neon") {
		return (
			<div style={{
				width: 84, height: 84, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center",
				clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
				background: "rgba(0,0,0,0.75)", border: "2px solid #ff00c8",
				fontSize: 36, fontWeight: 800, color: survey ? "#e9d5ff" : (correct ? "#00e5ff" : "#ff6ec7"),
				fontFamily: "Arial, Helvetica, sans-serif",
				boxShadow: survey ? "0 0 16px rgba(167,139,250,0.5)" : (correct ? "0 0 16px rgba(0,229,255,0.55)" : "0 0 16px rgba(255,0,200,0.55)"),
			}}>
				{survey ? "✓" : (correct ? "✓" : "✗")}
			</div>
		);
	}
	if (themeId === "minimal") {
		return (
			<div style={{
				width: 48, height: 48, margin: "0 auto 28px", borderRadius: "50%",
				border: `2px solid ${survey ? "#334155" : (correct ? "#2d5016" : "#7f1d1d")}`,
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: 20, color: survey ? "#334155" : (correct ? "#2d5016" : "#7f1d1d"),
				fontFamily: "Georgia, 'Times New Roman', serif",
			}}>
				{survey ? "—" : (correct ? "✓" : "×")}
			</div>
		);
	}
	if (themeId === "playful") {
		return (
			<div style={{
				width: 96, height: 96, margin: "0 auto 12px", borderRadius: "45% 55% 52% 48% / 48% 42% 58% 52%",
				background: survey ? "#7c3aed" : (correct ? "#ffd60a" : "#ff6b35"),
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: 48, color: survey ? "#fff" : (correct ? "#1e293b" : "#fff"),
				fontFamily: "Comic Sans MS, Chalkboard SE, cursive",
				boxShadow: `0 10px 0 ${survey ? "#5b21b6" : (correct ? "#e6b800" : "#c2410c")}`,
				animation: "er-qfm-wobble 2s ease-in-out infinite",
			}}>
				{survey ? "★" : (correct ? "★" : "!")}
			</div>
		);
	}
	if (themeId === "retro") {
		const label = survey ? "[ LOG ]" : (correct ? "[ OK!]" : "[ ERR]");
		return (
			<div style={{
				margin: "0 auto 16px", padding: "10px 16px", display: "inline-block",
				background: "#001100", border: "2px solid #33ff33",
				fontFamily: "Courier New, monospace", fontSize: 22, fontWeight: 700,
				color: survey ? "#33ff33" : (correct ? "#33ff33" : "#ff3333"),
				letterSpacing: "0.08em",
				boxShadow: "4px 4px 0 #000",
			}}>
				{label}
				<span style={{ animation: "er-qfm-blink 1s step-end infinite" }}>_</span>
			</div>
		);
	}
	if (themeId === "glass") {
		return (
			<div style={{
				width: 88, height: 88, margin: "0 auto 18px", borderRadius: "50%",
				background: "radial-gradient(circle at 35% 35%, #93c5fd, #1d4ed8 55%, #0c1929)",
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: 36, color: "#fff",
				border: "2px solid rgba(147,197,253,0.6)",
				animation: "er-qfm-bluePulse 2s ease-in-out infinite",
			}}>
				{survey ? "◉" : (correct ? "✦" : "✧")}
			</div>
		);
	}
	if (themeId === "cinematic") {
		return (
			<div style={{
				width: 64, height: 64, margin: "0 auto 16px",
				background: survey ? "#1e3a5f" : (correct ? "#065f46" : "#7f1d1d"),
				border: "2px solid rgba(255,255,255,0.25)",
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: 28, color: "#fff",
			}}>
				{survey ? "📋" : (correct ? "✚" : "⚠")}
			</div>
		);
	}
	if (themeId === "clinicalFormal") {
		const bg = survey ? "#10b981" : (correct ? "#10b981" : "#dc2626");
		const ring = survey || correct ? "#cbff97" : "#fecaca";
		return (
			<div style={{
				position: "relative",
				width: sz(96), height: sz(96), margin: `0 auto ${sz(20)}px`,
			}}>
				{!preview && (
					<div style={{
						position: "absolute", inset: 0, borderRadius: "50%",
						border: `2px solid ${bg}`,
						animation: "er-qfm-vitalRing 1.1s ease-out 0.35s both",
						pointerEvents: "none",
					}} />
				)}
				<div style={{
					width: "100%", height: "100%", borderRadius: "50%",
					background: bg,
					border: `${sz(4)}px solid ${ring}`,
					display: "flex", alignItems: "center", justifyContent: "center",
					fontSize: sz(42), color: "#fff", fontWeight: 700,
					fontFamily: "Oswald, Montserrat, sans-serif",
					boxShadow: survey || correct
						? "0 12px 32px rgba(16,185,129,0.35)"
						: "0 12px 32px rgba(220,38,38,0.3)",
					position: "relative", zIndex: 1,
				}}>
					{survey ? "✓" : (correct ? "✓" : "✗")}
				</div>
			</div>
		);
	}
	if (themeId === "comic") {
		return (
			<div style={{
				margin: "0 auto 10px", padding: "8px 20px", display: "inline-block",
				background: survey ? "#fde047" : (correct ? "#fde047" : "#f97316"),
				border: "4px solid #1e293b", borderRadius: 4,
				fontFamily: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
				fontSize: correct ? 36 : 32, fontWeight: 900, color: "#1e293b",
				transform: `rotate(${correct ? -4 : 5}deg)`,
				boxShadow: "6px 6px 0 #1e293b",
				lineHeight: 1,
			}}>
				{survey ? "OK!" : (correct ? "POW!" : "OOPS!")}
			</div>
		);
	}
	if (themeId === "horror") {
		return (
			<div style={{
				width: 80, height: 80, margin: "0 auto 14px", borderRadius: "50%",
				background: "radial-gradient(circle, #450a0a 0%, #0a0a0a 70%)",
				border: `3px solid ${correct ? "#7f1d1d" : "#dc2626"}`,
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: 36, color: correct ? "#fca5a5" : "#ef4444",
				fontFamily: "Palatino Linotype, Palatino, serif",
				animation: "er-qfm-horrorPulse 2s ease-in-out infinite",
			}}>
				{survey ? "☽" : (correct ? "†" : "☠")}
			</div>
		);
	}
	if (themeId === "gold") {
		return (
			<div style={{
				width: 84, height: 84, margin: "0 auto 16px", borderRadius: "50%",
				background: "linear-gradient(135deg, #fbbf24 0%, #fef3c7 40%, #d97706 100%)",
				border: "3px solid #fef3c7",
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: 40, color: "#1a1a1a",
				boxShadow: "0 0 30px rgba(251,191,36,0.5)",
			}}>
				{survey ? "★" : (correct ? "🏆" : "✦")}
			</div>
		);
	}
	if (themeId === "sticky") {
		return (
			<div style={{
				width: sz(52), height: sz(52), margin: `0 auto ${sz(12)}px`, borderRadius: "50%",
				background: correct ? "#86efac" : "#fca5a5",
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: sz(26), color: "#1e293b", fontWeight: 700,
				fontFamily: "Segoe Script, Bradley Hand, cursive",
				border: "2px dashed rgba(0,0,0,0.15)",
			}}>
				{survey ? "✓" : (correct ? "✓" : "✗")}
			</div>
		);
	}
	if (themeId === "corporate") {
		return (
			<div style={{
				width: sz(56), height: sz(56), margin: `0 auto ${sz(14)}px`, borderRadius: "50%",
				background: correct ? "#1e40af" : "#64748b",
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: sz(26), color: "#fff", fontWeight: 700,
			}}>
				{survey ? "✓" : (correct ? "✓" : "✗")}
			</div>
		);
	}
	if (themeId === "executive") {
		return (
			<div style={{
				width: sz(48), height: sz(48), margin: `${sz(36)}px auto ${sz(12)}px`, borderRadius: 2,
				border: `2px solid ${correct ? "#a1a1aa" : "#f87171"}`,
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: sz(22), color: correct ? "#fafafa" : "#fca5a5", fontWeight: 300,
				background: "rgba(0,0,0,0.2)",
			}}>
				{survey ? "◆" : (correct ? "✓" : "—")}
			</div>
		);
	}
	if (themeId === "institutional") {
		return (
			<div style={{
				width: sz(58), height: sz(58), margin: `0 auto ${sz(12)}px`, borderRadius: "50%",
				border: `3px double ${correct ? "#475569" : "#94a3b8"}`,
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: sz(9), fontWeight: 800, color: correct ? "#334155" : "#64748b",
				letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.1,
				background: "#f8fafc",
			}}>
				{survey ? "OK" : (correct ? "VÁLIDO" : "REVISAR")}
			</div>
		);
	}
	if (themeId === "academic") {
		return (
			<div style={{
				width: sz(54), height: sz(54), margin: `0 auto ${sz(14)}px`, borderRadius: "50%",
				background: correct ? "#7f1d1d" : "#57534e",
				border: `2px solid ${correct ? "#fef3c7" : "#d6d3d1"}`,
				display: "flex", alignItems: "center", justifyContent: "center",
				fontSize: sz(24), color: "#fffef9", fontFamily: "Georgia, serif",
			}}>
				{survey ? "✓" : (correct ? "A" : "—")}
			</div>
		);
	}
	// classic — blanco / verde-rojo
	return (
		<div style={{
			width: 88, height: 88, margin: "0 auto 18px", borderRadius: "50%",
			background: survey ? "#6366f1" : (correct ? "#10b981" : "#dc2626"),
			display: "flex", alignItems: "center", justifyContent: "center",
			fontSize: 44, color: "#fff", fontWeight: 800,
			boxShadow: survey ? "0 6px 0 #4338ca" : (correct ? "0 6px 0 #059669" : "0 6px 0 #991b1b"),
			border: "4px solid #fff",
			outline: `3px solid ${survey ? "#6366f1" : (correct ? "#10b981" : "#dc2626")}`,
		}}>
			{survey ? "✓" : (correct ? "✓" : "✗")}
		</div>
	);
}

function getThemeConfig(themeId, modal, translate) {
	const tr = typeof translate === "function" ? translate : (id) => id;
	const correct = modal.correct;
	const survey = modal.surveyMode;
	const t = resolveQuizFeedbackThemeId(themeId);
	const titleText = survey
		? (modal.surveySubmitMessage || tr("QUIZ.SURVEY_SENT"))
		: (correct ? tr("QUIZ.CORRECT") : tr("QUIZ.INCORRECT"));

	const configs = {
		classic: {
			overlay: { background: "rgba(15,23,42,0.55)" },
			panel: {
				background: "#ffffff", borderRadius: 16, padding: "40px 36px 32px", maxWidth: 380,
				boxShadow: "0 20px 60px rgba(0,0,0,0.28)",
				animation: correct || survey ? "er-qfm-stamp 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both" : "er-qfm-stampWrong 0.5s ease-out both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "Arial, Helvetica, sans-serif",
			title: { fontSize: 26, fontWeight: 800, color: "#1e293b", marginBottom: 6, textTransform: "none" },
			titleText,
			points: { fontSize: 20, fontWeight: 800, color: survey ? "#6366f1" : (correct ? "#059669" : "#dc2626"), marginBottom: 20 },
			summary: { background: correct || survey ? "#ecfdf5" : "#fef2f2", border: `2px solid ${correct || survey ? "#a7f3d0" : "#fecaca"}`, borderRadius: 10, color: "#334155" },
			button: { background: survey ? "#6366f1" : (correct ? "#10b981" : "#dc2626"), color: "#fff", borderRadius: 10, fontWeight: 800, border: "none", boxShadow: "0 4px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.04em" },
			buttonLabel: tr("QUIZ.CONTINUE"),
			progressCorrect: "#10b981", progressEmpty: "#d1fae5", progressText: "#64748b",
			explanation: { background: "#fff7ed", border: "2px solid #fdba74", color: "#9a3412", borderRadius: 10 },
		},
		neon: {
			overlay: { background: "radial-gradient(ellipse at center, rgba(88,28,135,0.5) 0%, rgba(2,6,23,0.92) 70%)" },
			panel: {
				background: "linear-gradient(180deg, #0a0014 0%, #12001f 100%)",
				borderRadius: 4, padding: "36px 28px", maxWidth: 400,
				border: "2px solid #ff00c8",
				animation: "er-qfm-neonEntry 0.7s ease-out both, er-qfm-neonBorder 2.5s ease-in-out infinite",
				position: "relative", overflow: "hidden",
			},
			panelFont: "Arial, Helvetica, sans-serif",
			title: {
				fontSize: 28, fontWeight: 800, color: "#ffffff", marginBottom: 10,
				textTransform: "uppercase", letterSpacing: "0.05em",
				textShadow: correct
					? "0 0 10px rgba(0,229,255,0.9), 0 2px 0 rgba(0,0,0,0.85)"
					: "0 0 10px rgba(255,0,200,0.85), 0 2px 0 rgba(0,0,0,0.85)",
			},
			titleText,
			points: {
				fontSize: 18, fontWeight: 700,
				color: survey ? "#e9d5ff" : (correct ? "#67e8f9" : "#fda4af"),
				marginBottom: 20,
				textShadow: "0 1px 3px rgba(0,0,0,0.9)",
			},
			summary: { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,0,200,0.4)", borderRadius: 0, color: "#f8fafc" },
			button: { background: "transparent", color: "#00e5ff", borderRadius: 0, fontWeight: 700, border: "2px solid #00e5ff", textTransform: "uppercase", letterSpacing: "0.08em" },
			buttonLabel: tr("QUIZ.CONTINUE"),
			progressCorrect: "#00e5ff", progressEmpty: "rgba(255,255,255,0.12)", progressText: "#e9d5ff",
			explanation: { background: "rgba(0,229,255,0.08)", border: "1px solid #00e5ff", color: "#e0f2fe", borderRadius: 0 },
		},
		minimal: {
			overlay: { background: "rgba(250,248,245,0.72)", backdropFilter: "blur(3px)" },
			panel: {
				background: "#fffef9", borderRadius: 2, padding: "44px 40px 36px", maxWidth: 360,
				border: "1px solid #1e293b", boxShadow: "none",
				animation: "er-qfm-paperRise 0.6s cubic-bezier(0.22, 1, 0.36, 1) both",
			},
			panelFont: "Georgia, 'Times New Roman', Times, serif",
			title: { fontSize: 22, fontWeight: 400, color: "#1e293b", marginBottom: 4, fontStyle: "italic" },
			titleText,
			points: { fontSize: 15, fontWeight: 400, color: "#57534e", marginBottom: 28, fontStyle: "italic" },
			summary: { background: "transparent", border: "none", borderTop: "1px solid #d6d3d1", borderRadius: 0, color: "#44403c", paddingTop: 16 },
			button: { background: "#1e293b", color: "#fffef9", borderRadius: 0, fontWeight: 400, border: "none", fontFamily: "Georgia, serif", letterSpacing: "0.06em" },
			buttonLabel: tr("QUIZ.FOLLOW"),
			progressCorrect: "#2d5016", progressEmpty: "#e7e5e4", progressText: "#78716c",
			explanation: { background: "#fafaf9", border: "1px solid #d6d3d1", color: "#292524", borderRadius: 0 },
		},
		playful: {
			overlay: { background: "rgba(255,45,149,0.2)" },
			panel: {
				background: "linear-gradient(160deg, #fff0f6 0%, #fffbeb 45%, #ecfeff 100%)",
				borderRadius: 36, padding: "32px 28px", maxWidth: 400,
				border: `5px solid ${survey ? "#7c3aed" : (correct ? "#ffd60a" : "#ff6b35")}`,
				animation: "er-qfm-carnivalSpin 0.75s cubic-bezier(0.34, 1.56, 0.64, 1) both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "Comic Sans MS, 'Segoe UI', Chalkboard SE, cursive",
			title: { fontSize: 30, fontWeight: 900, color: "#1e293b", marginBottom: 4, transform: "rotate(-2deg)" },
			titleText,
			points: { fontSize: 22, fontWeight: 900, color: survey ? "#7c3aed" : (correct ? "#ff2d95" : "#ff6b35"), marginBottom: 16 },
			summary: { background: "#fff", border: `3px dotted ${correct ? "#ffd60a" : "#ff6b35"}`, borderRadius: 24, color: "#475569" },
			button: {
				background: survey ? "#7c3aed" : (correct ? "#ff2d95" : "#ff6b35"),
				color: "#fff", borderRadius: 999, fontWeight: 900, fontSize: 18,
				border: "none", boxShadow: `0 8px 0 ${survey ? "#5b21b6" : (correct ? "#c2185b" : "#c2410c")}`,
			},
			buttonLabel: tr("QUIZ.LETS_GO"),
			progressCorrect: "#ff2d95", progressEmpty: "#fce7f3", progressText: "#64748b",
			explanation: { background: "#fef9c3", border: "3px solid #facc15", color: "#713f12", borderRadius: 20 },
		},
		retro: {
			overlay: { background: "rgba(0,0,0,0.88)" },
			panel: {
				background: "#0a0a0a", borderRadius: 0, padding: "24px 20px", maxWidth: 400,
				border: "3px solid #33ff33", boxShadow: "0 0 24px rgba(51,255,51,0.25), inset 0 0 40px rgba(0,0,0,0.8)",
				animation: correct || survey ? "er-qfm-fadeIn 0.2s both" : "er-qfm-terminalShake 0.55s ease both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "'Courier New', Courier, monospace",
			title: { fontSize: 18, fontWeight: 700, color: survey ? "#33ff33" : (correct ? "#33ff33" : "#ff3333"), marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" },
			titleText,
			points: { fontSize: 15, fontWeight: 700, color: survey ? "#33ff33" : (correct ? "#33ff33" : "#ff3333"), marginBottom: 20 },
			summary: { background: "#001100", border: "1px solid #33ff33", borderRadius: 0, color: "#33ff33" },
			button: { background: "#33ff33", color: "#000", borderRadius: 0, fontWeight: 700, border: "2px solid #33ff33", fontFamily: "inherit", textTransform: "uppercase" },
			buttonLabel: tr("QUIZ.ENTER"),
			progressCorrect: "#33ff33", progressEmpty: "#1a1a1a", progressText: "#33ff33",
			explanation: { background: "#001100", border: "1px dashed #33ff33", color: "#86efac", borderRadius: 0 },
		},
		glass: {
			overlay: { background: "linear-gradient(180deg, rgba(12,25,41,0.75) 0%, rgba(3,7,18,0.9) 100%)", backdropFilter: "blur(6px)" },
			panel: {
				background: "linear-gradient(145deg, rgba(30,58,95,0.85) 0%, rgba(12,25,41,0.92) 100%)",
				backdropFilter: "blur(16px)",
				borderRadius: 20, padding: "36px 32px", maxWidth: 400,
				border: "1px solid rgba(96,165,250,0.45)",
				boxShadow: "0 32px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(147,197,253,0.3)",
				animation: "er-qfm-blueRise 0.65s cubic-bezier(0.22, 1, 0.36, 1) both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "'Segoe UI', system-ui, sans-serif",
			title: { fontSize: 24, fontWeight: 700, color: "#f0f9ff", marginBottom: 6, letterSpacing: "0.02em" },
			titleText,
			points: { fontSize: 18, fontWeight: 600, color: survey ? "#93c5fd" : (correct ? "#60a5fa" : "#f87171"), marginBottom: 20 },
			summary: { background: "rgba(15,23,42,0.5)", border: "1px solid rgba(96,165,250,0.35)", borderRadius: 12, color: "#bfdbfe" },
			button: { background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)", color: "#fff", borderRadius: 12, fontWeight: 600, border: "1px solid #60a5fa", boxShadow: "0 4px 20px rgba(59,130,246,0.45)" },
			buttonLabel: tr("QUIZ.CONTINUE_MISSION"),
			progressCorrect: "#3b82f6", progressEmpty: "rgba(255,255,255,0.1)", progressText: "#93c5fd",
			explanation: { background: "rgba(30,58,138,0.4)", border: "1px solid #3b82f6", color: "#dbeafe", borderRadius: 12 },
		},
		cinematic: {
			overlay: { background: "rgba(15,76,92,0.82)" },
			panel: {
				background: "#0f4c5c",
				borderRadius: 4, padding: "20px 20px 28px", maxWidth: 420,
				border: "none",
				boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
				animation: "er-qfm-dossierSlide 0.55s cubic-bezier(0.22, 1, 0.36, 1) both",
				position: "relative", overflow: "hidden",
				...(correct || survey ? {} : { animation: "er-qfm-dossierSlide 0.55s cubic-bezier(0.22, 1, 0.36, 1) both, er-qfm-clinicalFlash 1.2s ease-in-out 3" }),
			},
			panelFont: "Arial Narrow, Arial, sans-serif",
			title: { fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.14em" },
			titleText,
			points: { fontSize: 16, fontWeight: 700, color: survey ? "#a7f3d0" : (correct ? "#6ee7b7" : "#fca5a5"), marginBottom: 16 },
			summary: { background: "#e8f4f8", border: "none", borderLeft: `4px solid ${correct ? "#059669" : "#dc2626"}`, borderRadius: 0, color: "#0f4c5c" },
			button: { background: "#fff", color: "#0f4c5c", borderRadius: 2, fontWeight: 700, border: "2px solid #fff", textTransform: "uppercase", letterSpacing: "0.1em" },
			buttonLabel: tr("QUIZ.CLOSE_REPORT"),
			progressCorrect: "#059669", progressEmpty: "rgba(255,255,255,0.2)", progressText: "#ccfbf1",
			explanation: { background: "#e8f4f8", border: "1px solid #99f6e4", color: "#134e4a", borderRadius: 2 },
		},
		clinicalFormal: {
			overlay: { background: survey || correct ? "rgba(6,78,59,0.45)" : "rgba(127,29,29,0.5)" },
			panel: {
				background: survey || correct ? "#ffffff" : "#fff5f5",
				borderRadius: 6,
				padding: "48px 44px 40px",
				maxWidth: 560,
				minWidth: 320,
				border: `1px solid ${survey || correct ? "#10b981" : "#fecaca"}`,
				boxShadow: survey || correct
					? "0 28px 80px rgba(16,185,129,0.22), 0 0 0 1px rgba(203,255,151,0.5)"
					: "0 28px 80px rgba(220,38,38,0.2)",
				animation: "er-qfm-clinicalFormalIn 0.85s cubic-bezier(0.22, 1, 0.36, 1) both",
				position: "relative",
				overflow: "hidden",
			},
			panelFont: "Montserrat, system-ui, sans-serif",
			title: {
				fontFamily: "Oswald, Montserrat, sans-serif",
				fontSize: 34,
				fontWeight: 600,
				color: survey || correct ? "#0a0a0a" : "#7f1d1d",
				marginBottom: 8,
				textTransform: "uppercase",
				letterSpacing: "0.06em",
			},
			titleText,
			points: {
				fontFamily: "Oswald, Montserrat, sans-serif",
				fontSize: 22,
				fontWeight: 600,
				color: survey ? "#10b981" : (correct ? "#10b981" : "#dc2626"),
				marginBottom: 24,
				letterSpacing: "0.04em",
			},
			summary: {
				background: survey || correct ? "#cbff97" : "#fee2e2",
				border: `1px solid ${survey || correct ? "#10b981" : "#fecaca"}`,
				borderRadius: 4,
				color: "#0a0a0a",
			},
			button: {
				background: survey || correct ? "#10b981" : "#dc2626",
				color: "#ffffff",
				borderRadius: 4,
				fontWeight: 700,
				border: "none",
				fontFamily: "Oswald, Montserrat, sans-serif",
				textTransform: "uppercase",
				letterSpacing: "0.1em",
				fontSize: 15,
				padding: "14px 28px",
				boxShadow: survey || correct ? "0 4px 0 #059669" : "0 4px 0 #991b1b",
			},
			buttonLabel: tr("QUIZ.CONTINUE"),
			progressCorrect: "#10b981",
			progressEmpty: "#e5e7eb",
			progressText: "#525252",
			explanation: {
				background: survey || correct ? "#f0fdf4" : "#fef2f2",
				border: `1px solid ${survey || correct ? "#cbff97" : "#fecaca"}`,
				color: "#0a0a0a",
				borderRadius: 4,
			},
		},
		comic: {
			overlay: { background: "rgba(30,41,59,0.65)" },
			panel: {
				background: "#ffffff", borderRadius: 4, padding: "28px 22px", maxWidth: 400,
				border: "5px solid #1e293b",
				boxShadow: "10px 10px 0 #1e293b",
				animation: "er-qfm-comicSlam 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
			title: { fontSize: 24, fontWeight: 900, color: "#1e293b", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" },
			titleText,
			points: {
				fontSize: 20, fontWeight: 900, color: "#1e293b", marginBottom: 16,
				background: correct ? "#fde047" : "#fdba74",
				display: "inline-block", padding: "6px 16px",
				border: "3px solid #1e293b", boxShadow: "4px 4px 0 #1e293b",
				letterSpacing: "0.05em",
			},
			summary: { background: "#fef9c3", border: "3px solid #1e293b", borderRadius: 0, color: "#1e293b" },
			summaryAccent: "#1e293b",
			button: { background: "#fde047", color: "#1e293b", borderRadius: 0, fontWeight: 900, border: "4px solid #1e293b", boxShadow: "4px 4px 0 #1e293b", textTransform: "uppercase" },
			buttonLabel: tr("QUIZ.NEXT_PANEL"),
			progressCorrect: "#fde047", progressEmpty: "#e2e8f0", progressText: "#475569",
			explanation: { background: "#fff", border: "3px solid #1e293b", color: "#1e293b", borderRadius: 0 },
		},
		horror: {
			overlay: { background: "radial-gradient(ellipse at center, rgba(69,10,10,0.6) 0%, rgba(0,0,0,0.92) 70%)" },
			panel: {
				background: "linear-gradient(180deg, #1a0505 0%, #0a0a0a 100%)",
				borderRadius: 2, padding: "32px 26px", maxWidth: 400,
				border: `2px solid ${correct ? "#7f1d1d" : "#dc2626"}`,
				boxShadow: "0 0 40px rgba(127,29,29,0.4)",
				animation: correct || survey ? "er-qfm-fadeIn 0.4s both" : "er-qfm-terminalShake 0.6s ease both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "Palatino Linotype, Palatino, 'Times New Roman', serif",
			title: { fontSize: 26, fontWeight: 700, color: correct ? "#fca5a5" : "#ef4444", marginBottom: 6, letterSpacing: "0.08em" },
			titleText,
			points: { fontSize: 16, fontWeight: 600, color: correct ? "#f87171" : "#dc2626", marginBottom: 20, fontStyle: "italic" },
			summary: { background: "rgba(69,10,10,0.4)", border: "1px solid #7f1d1d", borderRadius: 0, color: "#fecaca" },
			button: { background: "transparent", color: "#fca5a5", borderRadius: 0, fontWeight: 700, border: "2px solid #7f1d1d", letterSpacing: "0.1em" },
			buttonLabel: tr("QUIZ.CONTINUE_ELLIPSIS"),
			progressCorrect: "#b91c1c", progressEmpty: "rgba(255,255,255,0.08)", progressText: "#a8a29e",
			explanation: { background: "rgba(69,10,10,0.5)", border: "1px solid #991b1b", color: "#fecaca", borderRadius: 0 },
		},
		gold: {
			overlay: { background: "radial-gradient(ellipse at center, rgba(251,191,36,0.15) 0%, rgba(0,0,0,0.85) 65%)" },
			panel: {
				background: "linear-gradient(160deg, #1a1a1a 0%, #292524 50%, #1c1917 100%)",
				borderRadius: 12, padding: "36px 30px", maxWidth: 400,
				border: "2px solid #fbbf24",
				boxShadow: "0 24px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(254,243,199,0.2)",
				animation: "er-qfm-fadeIn 0.5s ease both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "Georgia, 'Times New Roman', serif",
			title: { fontSize: 26, fontWeight: 700, color: "#fef3c7", marginBottom: 6, letterSpacing: "0.06em" },
			titleText,
			points: { fontSize: 20, fontWeight: 700, color: correct ? "#fbbf24" : "#f87171", marginBottom: 20 },
			summary: { background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 8, color: "#fde68a" },
			button: {
				background: "linear-gradient(90deg, #b45309, #fbbf24, #fef3c7, #fbbf24, #b45309)",
				backgroundSize: "200% 100%",
				animation: "er-qfm-goldShine 3s linear infinite",
				color: "#1a1a1a", borderRadius: 8, fontWeight: 700, border: "1px solid #fef3c7",
			},
			buttonLabel: tr("QUIZ.CLAIM_GLORY"),
			progressCorrect: "#fbbf24", progressEmpty: "rgba(255,255,255,0.1)", progressText: "#d6d3d1",
			explanation: { background: "rgba(0,0,0,0.35)", border: "1px solid #d97706", color: "#fde68a", borderRadius: 8 },
		},
		sticky: {
			overlay: { background: "rgba(71,85,105,0.35)" },
			panel: {
				background: "linear-gradient(160deg, #fef9c3 0%, #fde047 100%)",
				borderRadius: 2, padding: "28px 22px 22px", maxWidth: 360,
				border: "none",
				boxShadow: "4px 6px 16px rgba(0,0,0,0.18), inset 0 -3px 8px rgba(0,0,0,0.06)",
				transform: "rotate(-2deg)",
				animation: "er-qfm-stickyDrop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both",
				position: "relative", overflow: "visible",
			},
			panelFont: "Segoe Script, Bradley Hand, 'Comic Sans MS', cursive",
			title: { fontSize: 22, fontWeight: 700, color: "#422006", marginBottom: 4 },
			titleText,
			points: { fontSize: 16, fontWeight: 600, color: correct ? "#15803d" : "#b45309", marginBottom: 16 },
			summary: { background: "rgba(255,255,255,0.35)", border: "none", borderTop: "1px dashed #ca8a04", borderRadius: 0, color: "#713f12" },
			button: { background: "#fef08a", color: "#422006", borderRadius: 4, fontWeight: 700, border: "2px solid #ca8a04", boxShadow: "2px 2px 0 rgba(0,0,0,0.1)" },
			buttonLabel: tr("QUIZ.OK_CHECK"),
			progressCorrect: "#ca8a04", progressEmpty: "rgba(255,255,255,0.5)", progressText: "#92400e",
			explanation: { background: "rgba(255,255,255,0.45)", border: "1px dashed #a16207", color: "#713f12", borderRadius: 2 },
		},
		corporate: {
			overlay: { background: "rgba(15,23,42,0.45)" },
			panel: {
				background: "#ffffff", borderRadius: 8, padding: "32px 28px 26px", maxWidth: 380,
				boxShadow: "0 16px 48px rgba(15,23,42,0.12)",
				border: "1px solid #e2e8f0",
				animation: "er-qfm-paperRise 0.45s ease both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "'Segoe UI', system-ui, Arial, sans-serif",
			title: { fontSize: 22, fontWeight: 600, color: "#0f172a", marginBottom: 6 },
			titleText,
			points: { fontSize: 16, fontWeight: 600, color: correct ? "#1e40af" : "#64748b", marginBottom: 20 },
			summary: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, color: "#334155" },
			button: { background: "#1e40af", color: "#fff", borderRadius: 6, fontWeight: 600, border: "none" },
			buttonLabel: tr("QUIZ.CONTINUE"),
			progressCorrect: "#1e40af", progressEmpty: "#e2e8f0", progressText: "#64748b",
			explanation: { background: "#f1f5f9", border: "1px solid #cbd5e1", color: "#334155", borderRadius: 6 },
		},
		executive: {
			overlay: { background: "rgba(0,0,0,0.55)" },
			panel: {
				background: "#ffffff", borderRadius: 4, padding: "0 24px 28px", maxWidth: 400,
				border: "1px solid #3f3f46",
				boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
				animation: "er-qfm-fadeIn 0.4s ease both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "'Segoe UI', system-ui, Arial, sans-serif",
			title: { fontSize: 20, fontWeight: 500, color: "#18181b", marginBottom: 6, letterSpacing: "0.02em" },
			titleText,
			points: { fontSize: 15, fontWeight: 500, color: correct ? "#3f3f46" : "#71717a", marginBottom: 20 },
			summary: { background: "#fafafa", border: "1px solid #e4e4e7", borderRadius: 2, color: "#3f3f46", margin: "0 20px" },
			button: { background: "#18181b", color: "#fafafa", borderRadius: 2, fontWeight: 500, border: "1px solid #3f3f46", letterSpacing: "0.04em" },
			buttonLabel: tr("QUIZ.PROCEED"),
			progressCorrect: "#52525b", progressEmpty: "#e4e4e7", progressText: "#71717a",
			explanation: { background: "#fafafa", border: "1px solid #d4d4d8", color: "#3f3f46", borderRadius: 2 },
		},
		institutional: {
			overlay: { background: "rgba(71,85,105,0.4)" },
			panel: {
				background: "#f8fafc", borderRadius: 2, padding: "28px 24px 24px", maxWidth: 380,
				border: "2px solid #94a3b8",
				boxShadow: "0 8px 24px rgba(15,23,42,0.1)",
				animation: "er-qfm-paperRise 0.5s ease both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "Arial, Helvetica, sans-serif",
			title: { fontSize: 18, fontWeight: 700, color: "#1e293b", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" },
			titleText,
			points: { fontSize: 15, fontWeight: 600, color: "#475569", marginBottom: 18 },
			summary: { background: "#fff", border: "1px solid #cbd5e1", borderRadius: 0, color: "#334155" },
			button: { background: "#475569", color: "#fff", borderRadius: 0, fontWeight: 700, border: "none", textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 14 },
			buttonLabel: tr("QUIZ.ACCEPT"),
			progressCorrect: "#475569", progressEmpty: "#e2e8f0", progressText: "#64748b",
			explanation: { background: "#fff", border: "1px solid #94a3b8", color: "#334155", borderRadius: 0 },
		},
		academic: {
			overlay: { background: "rgba(69,26,3,0.25)" },
			panel: {
				background: "linear-gradient(180deg, #fffef9 0%, #fffbeb 100%)",
				borderRadius: 4, padding: "30px 26px 24px", maxWidth: 380,
				border: "3px solid #7f1d1d",
				boxShadow: "0 12px 40px rgba(69,10,10,0.12)",
				animation: "er-qfm-paperRise 0.5s ease both",
				position: "relative", overflow: "hidden",
			},
			panelFont: "Georgia, 'Times New Roman', Times, serif",
			title: { fontSize: 22, fontWeight: 700, color: "#7f1d1d", marginBottom: 6 },
			titleText,
			points: { fontSize: 16, fontWeight: 600, color: correct ? "#7f1d1d" : "#57534e", marginBottom: 18, fontStyle: "italic" },
			summary: { background: "rgba(127,29,29,0.06)", border: "1px solid #fecaca", borderRadius: 4, color: "#44403c" },
			button: { background: "#7f1d1d", color: "#fffef9", borderRadius: 4, fontWeight: 600, border: "none", fontFamily: "Georgia, serif" },
			buttonLabel: tr("QUIZ.CONTINUE"),
			progressCorrect: "#7f1d1d", progressEmpty: "#fde68a", progressText: "#78716c",
			explanation: { background: "#fffef9", border: "1px solid #d6d3d1", color: "#44403c", borderRadius: 4 },
		},
	};

	return configs[t] || configs.classic;
}

export { getThemeConfig };

export function renderFeedbackThemeIcon(themeId, opts) {
	return renderIcon(themeId, opts);
}

export function FeedbackThemeDecorations(props) {
	return <ThemeDecorations {...props} />;
}

export default function QuizFeedbackModal({ modal, onClose, previewNote, overlayZIndex = 9999 }) {
	const intl = useIntl();
	const tr = (id, values) => intl.formatMessage({ id }, values);
	if (!modal) return null;

	const themeId = resolveQuizFeedbackThemeId(modal.theme);
	if (themeId === "clinicalFormal") ensureClinicalFormalFonts();
	const cfg = getThemeConfig(themeId, modal, tr);
	const { correct, delta, surveyMode, totalCorrect, correctCount, done, totalPoints, incorrectCount, explanation } = modal;
	const showProgress = !surveyMode && totalCorrect > 1 && !done;
	const pointsMargin = showProgress ? 8 : (cfg.points.marginBottom ?? 24);
	const progressShape = themeId === "retro" || themeId === "neon" || themeId === "comic" ? "square" : (themeId === "playful" || themeId === "sticky" ? "blob" : "circle");

	return (
		<>
			<style>{KEYFRAMES}</style>
			<div
				className="quiz-feedback-overlay"
				style={{
					position: "fixed", inset: 0, zIndex: overlayZIndex,
					display: "flex", alignItems: "center", justifyContent: "center",
					animation: "er-qfm-fadeIn 0.25s ease-out",
					...cfg.overlay,
				}}
			>
				<div
					className="quiz-feedback-modal"
					style={{
						textAlign: "center",
						fontFamily: cfg.panelFont,
						color: cfg.panel.color,
						position: "relative",
						zIndex: 1,
						...cfg.panel,
						...(themeId === "cinematic" && !correct && !surveyMode ? { borderLeft: "6px solid #dc2626" } : {}),
					}}
				>
					<ThemeDecorations themeId={themeId} correct={correct} survey={surveyMode} />
					{themeId === "cinematic" && (
						<div style={{ background: "#e8f4f8", margin: "-20px -20px 16px", padding: "16px 20px", textAlign: "left", borderBottom: "2px solid rgba(15,76,92,0.2)" }}>
							<div style={{ fontSize: 10, fontWeight: 700, color: "#0f4c5c", letterSpacing: "0.18em", marginBottom: 4 }}>LABORATORIO · RESULTADO</div>
							<div style={{ fontSize: 11, color: "#64748b" }}>Evaluación de muestra</div>
						</div>
					)}
					{themeId === "clinicalFormal" && (
						<div style={{
							background: surveyMode || correct ? "#cbff97" : "#fee2e2",
							margin: "-48px -44px 28px",
							padding: "22px 44px 18px",
							textAlign: "left",
							borderBottom: `2px solid ${surveyMode || correct ? "#10b981" : "#fecaca"}`,
						}}>
							<div style={{
								fontFamily: "Oswald, Montserrat, sans-serif",
								fontSize: 12,
								fontWeight: 600,
								color: "#0a0a0a",
								letterSpacing: "0.2em",
								textTransform: "uppercase",
								marginBottom: 4,
							}}>
								Informe clínico · Evaluación
							</div>
							<div style={{
								fontFamily: "Montserrat, system-ui, sans-serif",
								fontSize: 13,
								fontWeight: 500,
								color: surveyMode || correct ? "#065f46" : "#991b1b",
							}}>
								{surveyMode ? "Registro de respuesta" : (correct ? "Resultado válido" : "Resultado a revisar")}
							</div>
						</div>
					)}
					{themeId === "executive" && (
						<div style={{ background: "linear-gradient(180deg, #27272a 0%, #18181b 100%)", margin: "0 -24px 16px", padding: "20px 24px 14px", textAlign: "left" }}>
							<div style={{ fontSize: 11, color: "#a1a1aa", letterSpacing: "0.14em", textTransform: "uppercase" }}>Informe ejecutivo</div>
						</div>
					)}
					{renderIcon(themeId, { correct, survey: surveyMode })}
					<div style={cfg.title}>{cfg.titleText}</div>
					{surveyMode ? (
						<div style={{ ...cfg.points, marginBottom: 24 }}>
							{previewNote || tr("QUIZ.SURVEY_THANKS")}
						</div>
					) : (
						<div style={{ ...cfg.points, marginBottom: pointsMargin }}>
							{themeId === "retro" ? `SCORE: ${delta >= 0 ? "+" : ""}${delta}`
								: themeId === "comic" ? `+${delta} PTS!`
								: themeId === "gold" ? `+${delta} puntos de honor`
								: themeId === "sticky" ? `+${delta} pts ✎`
								: `${delta >= 0 ? "+" : ""}${delta} puntos`}
						</div>
					)}
					{showProgress && (
						<div style={{ marginBottom: 24 }}>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 10 }}>
								{Array.from({ length: totalCorrect }).map((_, i) => (
									<div
										key={i}
										style={{
											width: 36, height: 36,
											borderRadius: progressShape === "square" ? 0 : (progressShape === "blob" ? "40% 60% 55% 45% / 50% 45% 55% 50%" : "50%"),
											display: "flex", alignItems: "center", justifyContent: "center",
											backgroundColor: i < correctCount ? cfg.progressCorrect : cfg.progressEmpty,
											color: i < correctCount ? "#fff" : cfg.progressText,
											fontSize: 16, fontWeight: 700,
											border: i < correctCount ? "none" : `2px solid ${cfg.progressEmpty}`,
											fontFamily: cfg.panelFont,
										}}
									>
										{i < correctCount ? (themeId === "retro" ? "Y" : "✓") : ""}
									</div>
								))}
							</div>
							<div style={{ fontSize: 14, color: cfg.progressText, fontWeight: 500 }}>
								<span style={{ color: cfg.progressCorrect, fontWeight: 700 }}>{correctCount}</span> de {totalCorrect}
								{themeId !== "minimal" && themeId !== "retro" ? ` — faltan ${totalCorrect - correctCount}` : ""}
							</div>
						</div>
					)}
					{done && !surveyMode && (
						<div style={{ marginBottom: 24, padding: "16px 20px", ...cfg.summary }}>
							<div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, opacity: 0.85, letterSpacing: themeId === "cinematic" ? "0.1em" : undefined }}>
								{themeId === "retro" ? "FINAL_SCORE" : themeId === "comic" ? "RESUMEN" : themeId === "horror" ? "Destino" : themeId === "gold" ? "Balance" : tr("QUIZ.QUIZ_RESULT")}
							</div>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
								<span style={{ fontSize: 15 }}>
									<span style={{ color: cfg.summaryAccent ?? cfg.progressCorrect, fontWeight: 700 }}>{correctCount}</span> acertadas
								</span>
								{incorrectCount > 0 && (
									<span style={{ fontSize: 15 }}>
										<span style={{ color: themeId === "neon" ? "#ff00c8" : "#ef4444", fontWeight: 700 }}>{incorrectCount}</span> falladas
									</span>
								)}
								<span style={{ fontSize: 18, fontWeight: 700, color: totalPoints >= 0 ? (cfg.summaryAccent ?? cfg.progressCorrect) : (themeId === "neon" ? "#ff00c8" : "#ef4444") }}>
									Total: {totalPoints >= 0 ? "+" : ""}{totalPoints} pts
								</span>
							</div>
						</div>
					)}
					{explanation && !surveyMode && (
						<div style={{ marginBottom: 24, padding: "16px 20px", textAlign: "left", ...cfg.explanation }}>
							<div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
								{themeId === "cinematic" || themeId === "clinicalFormal" ? "Notas clínicas" : themeId === "retro" ? "> NOTE" : themeId === "comic" ? "¡Bocadillo!" : themeId === "horror" ? "Susurro…" : themeId === "gold" ? "Nota del jurado" : themeId === "sticky" ? "Nota al margen" : tr("QUIZ.EXPLANATION")}
							</div>
							<div style={{ fontSize: 15, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{explanation}</div>
						</div>
					)}
					<button
						type="button"
						onClick={onClose}
						style={{
							padding: "12px 32px", fontSize: 16, cursor: "pointer",
							fontFamily: cfg.panelFont,
							transition: "transform 0.12s, box-shadow 0.12s",
							...cfg.button,
						}}
						onMouseOver={(e) => { e.currentTarget.style.transform = "scale(1.03)"; }}
						onMouseOut={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
					>
						{cfg.buttonLabel}
					</button>
				</div>
			</div>
		</>
	);
}
